import debounce from "lodash.debounce";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { fetchGet, toQueryString } from "../../../service/api_client";
import { WarehouseModel } from "../models/warehouse_model";
import POAutoCompleteField from "../../purchase_order/purchase_order_create/components/po_auto_complete_widget";

interface WarehouseSearchFieldProps {
  onSelect: (data: WarehouseModel) => void;
  onClear?: () => void;
  disabled?: boolean;
  placeholder?: string;
  value?: WarehouseModel;
  type?: string;
  id?: string;
}

const WarehouseSearchField: FunctionComponent<WarehouseSearchFieldProps> = (
  props,
) => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/warehouse_type`;
  const { showToast } = useToast();
  const [edit, setEdit] = useState(!(props.disabled ?? false));
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState<string | null>(null);
  const [data, setData] = useState<WarehouseModel[]>();

  const getData = async (prop: {
    type?: string;
    search?: string;
    page: number;
    count: number;
    all?: boolean;
  }) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + toQueryString(prop));
      if (res.success) {
        setData(res.data.data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    }
    setLoading(false);
  };

  const debouncedHandler = useCallback(
    debounce((query) => {
      if (query)
        getData({
          search: query.trim(),
          page: 1,
          count: 1,
          all: true,
          type: props?.type,
        });
      else setData(undefined);
    }, 500),
    [],
  );

  useEffect(() => {
    if (search && edit) {
      if (props.value && props.value.name === search) return;
      debouncedHandler(search);
    }
  }, [search]);

  useEffect(() => {
    setEdit(!(props.disabled ?? false));
  }, [props.disabled]);

  useEffect(() => {
    if (props.value) {
      setSearch(props.value.name);
      setEdit(false); // force read-only display
    } else {
      setSearch(null);
    }
  }, [props.value]);

  return (
    <div className="flex gap-1 items-center  w-full">
      {edit ? (
        <POAutoCompleteField
          id={props.id}
          loading={loading}
          value={search || ""}
          placeholder={props.placeholder ?? "Search"}
          onSelect={(val) => {
            setEdit(false);
            setSearch(val.value.name);
            props.onSelect(val.value);
          }}
          suggestions={data?.map((d) => ({
            label: (
              <div className="text-xs">
                <b> {d.name} </b>
              </div>
            ),

            value: d,
          }))}
          onChange={(newValue) => {
            if (newValue.trim() === "") {
              setSearch(null);
              props.onClear?.();
              return;
            } else {
              setSearch(newValue);
            }
          }}
        />
      ) : (
        <div
          id={props.id}
          title={search || ""}
          className={`text-sm px-3 py-2  h-8 rounded focus:outline-none w-full border overflow-hidden overflow-ellipsis ${
            search ? "" : " text-gray-400 "
          }`}
          onClick={() => {
            if (!props.disabled) {
              setEdit(true);
              // setSearch(emp?.name ?? "");
            }
          }}
        >
          {search || props.placeholder || "Search"}
        </div>
      )}
    </div>
  );
};

export default WarehouseSearchField;
